export const DEFAULT_THEME_NAME = "DarkSpacesTheme" as const;
export const THEME_STORAGE_KEY = "appTheme";
export const REDUX_PERSIST_STORAGE_KEY = "persist:nextjs";

export const THEME_NAMES = [
  "PureLightTheme",
  "GreyGooseTheme",
  "PurpleFlowTheme",
  "DarkSpacesTheme",
  "GreenFieldsTheme",
  "NebulaFighterTheme"
] as const;

export type ThemeName = (typeof THEME_NAMES)[number];
export type ThemeMode = "light" | "dark";

export const THEME_MODE_BY_NAME: Record<ThemeName, ThemeMode> = {
  PureLightTheme: "light",
  GreyGooseTheme: "light",
  PurpleFlowTheme: "light",
  DarkSpacesTheme: "dark",
  GreenFieldsTheme: "dark",
  NebulaFighterTheme: "dark"
};

export const THEME_BACKGROUND_BY_NAME: Record<ThemeName, string> = {
  PureLightTheme: "#dee4eb",
  GreyGooseTheme: "#F8F8F8",
  PurpleFlowTheme: "#ffffff",
  DarkSpacesTheme: "#353b41",
  GreenFieldsTheme: "#141c23",
  NebulaFighterTheme: "#070C27"
};

interface StorageLike {
  getItem(key: string): string | null;
}

function safeParseJson<T>(value: string | null): T | null {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

export function isThemeName(value: unknown): value is ThemeName {
  return typeof value === "string" && (THEME_NAMES as readonly string[]).includes(value);
}

function readThemeFromPersistedRoot(persistedRoot: Record<string, string> | null): ThemeName | null {
  if (!persistedRoot) {
    return null;
  }

  const changeTheme = safeParseJson<{ curTheme?: unknown }>(persistedRoot.changeTheme ?? null);
  if (isThemeName(changeTheme?.curTheme)) {
    return changeTheme.curTheme;
  }

  const auth = safeParseJson<{ user?: { preferences?: { theme_id?: unknown } } }>(persistedRoot.auth ?? null);
  if (isThemeName(auth?.user?.preferences?.theme_id)) {
    return auth.user.preferences.theme_id;
  }

  return null;
}

export function resolveThemeNameFromStorage(storage: StorageLike | null | undefined): ThemeName {
  if (!storage) {
    return DEFAULT_THEME_NAME;
  }

  const persistedRoot = safeParseJson<Record<string, string>>(storage.getItem(REDUX_PERSIST_STORAGE_KEY));
  const persistedTheme = readThemeFromPersistedRoot(persistedRoot);
  if (persistedTheme) {
    return persistedTheme;
  }

  const legacyTheme = storage.getItem(THEME_STORAGE_KEY);
  if (isThemeName(legacyTheme)) {
    return legacyTheme;
  }

  return DEFAULT_THEME_NAME;
}

export function getThemeMode(themeName: ThemeName): ThemeMode {
  return THEME_MODE_BY_NAME[themeName];
}

export function resolveInitialThemeName(): ThemeName {
  if (typeof window === "undefined" || !window.localStorage) {
    return DEFAULT_THEME_NAME;
  }

  return resolveThemeNameFromStorage(window.localStorage);
}
